import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, download, query, upload } from '../api/client'
import { useQuery } from '../api/hooks'
import type {
  ImportHistoryRow,
  ImportKind,
  ImportKindInfo,
  ImportPreview,
  ImportResult,
} from '../api/types'
import {
  Empty,
  ErrorBanner,
  Loading,
  Money,
  Panel,
  StatusTag,
  Tag,
  formatDateTime,
  isoDate,
  money,
} from '../components/ui'

/**
 * Excel import (BUILD_PLAN §1 row 16, §6 step 7) — the other half of a requirement whose export
 * side has been complete since the first month.
 *
 * <p>The screen is deliberately two steps rather than one. Uploading <em>checks</em> the file and
 * writes nothing; a second, separate click commits it. That is what makes "identify missing or
 * incorrect information" something the office reads before deciding, instead of something they
 * discover in the ledger afterwards.</p>
 */
export function ImportPage() {
  const [kind, setKind] = useState<ImportKind>('ChartOfAccounts')
  const [postingDate, setPostingDate] = useState(isoDate(new Date()))
  const [description, setDescription] = useState('Opening balances')
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [committed, setCommitted] = useState<ImportResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()
  const fileInput = useRef<HTMLInputElement>(null)

  const kinds = useQuery<ImportKindInfo[]>('/imports/kinds')
  const history = useQuery<ImportHistoryRow[]>(`/imports${query({ limit: 50 })}`)

  const available = (kinds.data ?? []).filter((k) => k.allowed)
  const chosen = available.find((k) => k.kind === kind) ?? available[0]

  // The ledger kinds need a date and a description; the master-data ones have nothing to ask for.
  const needsEntryDetails = chosen?.kind === 'OpeningBalances'

  function reset() {
    setPreview(null)
    setCommitted(null)
    setError(undefined)
    if (fileInput.current) {
      fileInput.current.value = ''
    }
  }

  /** Step one: check the file. Writes a batch and its error log, and nothing else. */
  async function check(file: File) {
    if (!chosen) return

    setBusy(true)
    setError(undefined)
    setCommitted(null)

    try {
      const form = new FormData()
      form.append('file', file)

      if (needsEntryDetails) {
        form.append('postingDate', postingDate)
        form.append('description', description)
      }

      setPreview(await upload<ImportPreview>(`/imports/${chosen.kind}/validate`, form))
    } catch (caught) {
      setError(caught)
      setPreview(null)
    } finally {
      setBusy(false)
      history.reload()
    }
  }

  /** Step two, and the first click that changes anything. */
  async function commit() {
    if (!preview) return

    setBusy(true)
    setError(undefined)

    try {
      setCommitted(await api<ImportResult>(`/imports/${preview.batchId}/commit`, { method: 'POST' }))
      setPreview(null)
      if (fileInput.current) {
        fileInput.current.value = ''
      }
    } catch (caught) {
      setError(caught)
      // The batch records why it was refused, so re-read it rather than leaving a stale preview
      // claiming the file is fine.
      try {
        setPreview(await api<ImportPreview>(`/imports/${preview.batchId}`))
      } catch {
        setPreview(null)
      }
    } finally {
      setBusy(false)
      history.reload()
    }
  }

  async function discard() {
    if (!preview) return

    setBusy(true)
    try {
      await api(`/imports/${preview.batchId}/discard`, { method: 'POST' })
      reset()
    } catch (caught) {
      setError(caught)
    } finally {
      setBusy(false)
      history.reload()
    }
  }

  if (kinds.loading) {
    return <Loading what="Loading the import kinds" />
  }

  if (available.length === 0) {
    return (
      <div className="banner warn">
        You do not have permission to import anything. Importing accounts is creating accounts, and
        importing opening balances is posting an entry — the permission needed is the permission for
        what the file would do.
      </div>
    )
  }

  return (
    <>
      <div className="toolbar" style={{ marginBottom: 14 }}>
        <div className="field">
          <label>What are you importing?</label>
          <select
            value={chosen?.kind}
            onChange={(e) => {
              setKind(e.target.value as ImportKind)
              reset()
            }}
          >
            {available.map((k) => (
              <option key={k.kind} value={k.kind}>
                {k.sheet}
              </option>
            ))}
          </select>
        </div>

        {needsEntryDetails && (
          <>
            <div className="field">
              <label>Posting date</label>
              <input
                type="date"
                value={postingDate}
                onChange={(e) => setPostingDate(e.target.value)}
              />
            </div>
            <div className="field">
              <label>Description</label>
              <input value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
          </>
        )}

        <div className="spacer" />

        <button
          onClick={() =>
            void download(`/imports/templates/${chosen?.kind}`, `${chosen?.kind}-template.xlsx`)
          }
        >
          Download template
        </button>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(undefined)} />
      <ErrorBanner error={kinds.error} />

      <Panel title={chosen?.sheet}>
        <p className="soft" style={{ marginTop: 0 }}>
          {chosen?.purpose}
        </p>

        <p className="muted" style={{ fontSize: 12 }}>
          Uploading <strong>checks</strong> the file and writes nothing. You get a second button once
          it passes.
        </p>

        <input
          ref={fileInput}
          type="file"
          accept=".xlsx,.xlsm"
          disabled={busy}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) void check(file)
          }}
        />

        {busy && <Loading what="Checking the file" />}

        {/* The columns, from the server rather than restated here — the template, the parser and
            this list are the three things that have to agree, and one of them owns the truth. */}
        <details style={{ marginTop: 12 }}>
          <summary className="soft" style={{ cursor: 'pointer', fontSize: 13 }}>
            Columns this sheet expects
          </summary>
          <div className="table-scroll" style={{ marginTop: 8 }}>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 180 }}>Column</th>
                  <th style={{ width: 90 }}>Required</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {(chosen?.columns ?? []).map((column) => (
                  <tr key={column.heading}>
                    <td className="strong">{column.heading}</td>
                    <td>{column.required ? <Tag kind="warn">Required</Tag> : ''}</td>
                    <td className="soft">{column.help ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      </Panel>

      {committed && (
        <div className="banner ok">
          <strong>Imported.</strong> {committed.committedCount} row
          {committed.committedCount === 1 ? '' : 's'} written.{' '}
          {committed.journalEntryId && (
            <>
              Posted as{' '}
              <Link to={`/journal/${committed.journalEntryId}`}>
                {committed.entryNo ?? 'the opening entry'}
              </Link>
              .
            </>
          )}
        </div>
      )}

      {preview && <PreviewPanel preview={preview} busy={busy} onCommit={commit} onDiscard={discard} />}

      <Panel flush title="Import history">
        {history.loading && <Loading />}

        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th style={{ width: 150 }}>When</th>
                <th style={{ width: 130 }}>Kind</th>
                <th>File</th>
                <th style={{ width: 100 }}>Status</th>
                <th style={{ width: 70 }} className="num">
                  Rows
                </th>
                <th style={{ width: 70 }} className="num">
                  Errors
                </th>
                <th style={{ width: 90 }} className="num">
                  Written
                </th>
                <th style={{ width: 160 }}>By</th>
              </tr>
            </thead>
            <tbody>
              {(history.data ?? []).length === 0 && !history.loading && (
                <tr>
                  <td colSpan={8}>
                    <Empty>Nothing has been imported yet.</Empty>
                  </td>
                </tr>
              )}

              {(history.data ?? []).map((row) => (
                <tr key={row.id}>
                  <td className="soft">{formatDateTime(row.createdAt)}</td>
                  <td>{row.kind}</td>
                  <td>
                    {row.fileName}
                    {row.entryNo && (
                      <>
                        {' · '}
                        <Link to={`/journal/${row.journalEntryId}`} className="code">
                          {row.entryNo}
                        </Link>
                      </>
                    )}
                  </td>
                  <td>
                    <StatusTag status={row.status} />
                  </td>
                  <td className="num">{row.rowCount}</td>
                  <td className={row.errorCount > 0 ? 'num negative' : 'num'}>
                    {row.errorCount || ''}
                  </td>
                  <td className="num strong">{row.committedCount || ''}</td>
                  <td className="soft">{row.committedBy ?? row.createdBy ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  )
}

/**
 * What the file was found to contain, and the error log.
 *
 * <p>The issues are listed by sheet row rather than summarised, because the person fixing the file
 * is looking at Excel: "row 47, Code" is the same fact as "one row is wrong" with the search
 * already done.</p>
 */
function PreviewPanel({
  preview,
  busy,
  onCommit,
  onDiscard,
}: {
  preview: ImportPreview
  busy: boolean
  onCommit: () => Promise<void>
  onDiscard: () => Promise<void>
}) {
  const balanced =
    preview.totalDebit === undefined ||
    preview.totalDebit === null ||
    Math.abs((preview.totalDebit ?? 0) - (preview.totalCredit ?? 0)) < 0.005

  return (
    <Panel
      title={preview.fileName}
      actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="subtle" disabled={busy} onClick={() => void onDiscard()}>
            Discard
          </button>
          {preview.isCommittable && (
            <button className="primary" disabled={busy} onClick={() => void onCommit()}>
              {busy ? 'Importing…' : `Import ${preview.rowCount} row${preview.rowCount === 1 ? '' : 's'}`}
            </button>
          )}
        </div>
      }
    >
      {preview.isCommittable ? (
        <div className="banner ok">
          <strong>{preview.rowCount} rows checked, nothing wrong.</strong> Nothing has been written
          yet — use the button above.
        </div>
      ) : (
        <div className="banner error">
          <strong>
            {preview.errorCount} problem{preview.errorCount === 1 ? '' : 's'} in {preview.fileName}.
          </strong>{' '}
          Nothing was written. Fix the rows below and upload the file again.
        </div>
      )}

      {preview.warningCount > 0 && (
        <div className="banner warn">
          {preview.warningCount} warning{preview.warningCount === 1 ? '' : 's'} — worth reading, and
          they do not stop the import.
        </div>
      )}

      {/* Opening balances only. The one thing that has to be true about the file, stated rather
          than inferred from the absence of an error. */}
      {preview.totalDebit !== undefined && preview.totalDebit !== null && (
        <p className={balanced ? 'positive' : 'negative'}>
          Debits <Money value={preview.totalDebit} /> · Credits{' '}
          <Money value={preview.totalCredit ?? 0} />
          {!balanced && ` · out by ${money(Math.abs(preview.totalDebit - (preview.totalCredit ?? 0)))}`}
        </p>
      )}

      {preview.issues.length > 0 && (
        <div className="table-scroll" style={{ marginBottom: 14 }}>
          <table>
            <thead>
              <tr>
                <th style={{ width: 70 }}>Row</th>
                <th style={{ width: 150 }}>Column</th>
                <th>What is wrong</th>
              </tr>
            </thead>
            <tbody>
              {preview.issues.map((issue, index) => (
                <tr key={`${issue.rowNumber}-${issue.code}-${index}`}>
                  <td className="code">{issue.rowNumber === 0 ? 'file' : issue.rowNumber}</td>
                  <td className="soft">{issue.column ?? ''}</td>
                  <td className={issue.severity === 'Error' ? 'negative' : ''}>
                    {issue.severity === 'Warning' && <Tag kind="warn">Warning</Tag>} {issue.message}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* The first rows as they were read. This is what proves column D really did land in
          "Credit" before anything is written, which no amount of validation can say. */}
      {preview.sample.length > 0 && (
        <details>
          <summary className="soft" style={{ cursor: 'pointer', fontSize: 13 }}>
            The first {preview.sample.length} rows, as they were read
          </summary>
          <div className="table-scroll" style={{ marginTop: 8 }}>
            <table>
              <thead>
                <tr>
                  {preview.columns.map((column) => (
                    <th key={column}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.sample.map((row, index) => (
                  <tr key={index}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex}>{cell ?? ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </Panel>
  )
}
